import { Box, Button, Group, Loader, Modal, Progress, ScrollArea, Stack, Text, Textarea, ThemeIcon, useMantineTheme } from "@mantine/core";

import { IconCode, IconSparkles } from "@tabler/icons-react";

import { useMediaQuery } from "@mantine/hooks";


import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import trpc from "@/shared/utils/trpc";

import type { IntentAnswers, IntentQuestion } from "@shared/companyIntent.types";
import { INTENT_FOLLOW_UP_MAX_CHARS } from "@shared/companyIntent.types";

import {
  Q15_GROUP_PRIORITY_COUNTRIES,
  Q15_PRIORITY_MARKET_ID,
  QUESTIONNAIRE_FOLLOW_UP,
  getIntentQuestionDefinition,
  getMaxSelections,
  getSectionLabel,
  hasAnswerGroups,
  resolveIntentSelectionMode,
} from "@shared/intentQuestionnaireDimensions";

import { resolveCollaborationRegionIds } from "@shared/collaborationRegionsCatalog";

import { toIntentAnswerPayload, parseIntentAnswer, collectIntentFollowUpTexts } from "@shared/companyIntentAnswers.util";

import { intentQuestionsToJsonSource } from "@shared/adminIntentQuestionnaireJson";

import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

import { successNotification } from "@/shared/utils/successNotification";

import AiGenerationErrorState from "@/components/shared/AiGenerationErrorState";

import AdminIntentQuestionnaireJsonModal from "@/pages/admin/company-lists/components/AdminIntentQuestionnaireJsonModal";

import IntentQuestionCard, {

  isQuestionAnswerStateComplete,

  type QuestionAnswerState,

} from "./IntentQuestionCard";



export interface CompanyIntentQuestionnaireModalProps {

  opened: boolean;

  onClose: () => void;

  /** Called after a successful submit (instead of onClose). */

  onSubmitted?: () => void;

  /** When set, loads/submits for a facilitator-managed company (same UI as company users). */

  managedCompanyId?: string;

  /** When set, loads/generates/submits the 15-question onboarding Q&A for an admin-selected company. */

  adminCompanyId?: string;

  /** Admin-only: questionnaire is already submitted, so the caller can open the edit view instead. */

  onAlreadyCompleted?: () => void;

  /** Admin-only: run AI auto-fill once questions are ready. */

  autoFillOnReady?: boolean;

}



const GENERATE_TIMEOUT_MS = 120_000;



type LoadPhase = "idle" | "loading" | "generating" | "ready" | "error";



const EMPTY_ANSWER: QuestionAnswerState = { selectedIds: [] };

function intentAnswersToUiState(answers: IntentAnswers): Record<string, QuestionAnswerState> {
  const next: Record<string, QuestionAnswerState> = {};
  for (const [questionId, value] of Object.entries(answers)) {
    const parsed = parseIntentAnswer(value);
    next[questionId] = {
      selectedIds: parsed.selectedIds,
      followUpText: parsed.followUpText,
      groupSelections: parsed.groups,
      groupRegionIds: parsed.regionIds?.length
        ? { [Q15_GROUP_PRIORITY_COUNTRIES]: parsed.regionIds }
        : undefined,
      conditionalAnswers: parsed.conditional,
    };
  }
  return next;
}



function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {

  return new Promise<T>((resolve, reject) => {

    const timer = window.setTimeout(() => {

      reject(new Error(`${label} timed out after ${Math.round(timeoutMs / 1000)}s`));

    }, timeoutMs);



    promise

      .then((value) => {

        window.clearTimeout(timer);

        resolve(value);

      })

      .catch((err) => {

        window.clearTimeout(timer);

        reject(err);

      });

  });

}



function getSectionHeader(question: IntentQuestion, prevQuestion?: IntentQuestion): string | null {

  const section = getIntentQuestionDefinition(question.id)?.section;

  if (!section) return null;

  const prevSection = prevQuestion

    ? getIntentQuestionDefinition(prevQuestion.id)?.section

    : undefined;

  if (section === prevSection) return null;

  return getSectionLabel(section);

}



export default function CompanyIntentQuestionnaireModal({

  opened,

  onClose,

  onSubmitted,

  managedCompanyId,

  adminCompanyId,

  onAlreadyCompleted,

  autoFillOnReady = false,

}: CompanyIntentQuestionnaireModalProps) {

  const [questions, setQuestions] = useState<IntentQuestion[] | null>(null);

  const [answers, setAnswers] = useState<Record<string, QuestionAnswerState>>({});

  const [questionnaireFollowUpText, setQuestionnaireFollowUpText] = useState("");

  const [loadPhase, setLoadPhase] = useState<LoadPhase>("idle");

  const [submitting, setSubmitting] = useState(false);

  const [retryNonce, setRetryNonce] = useState(0);

  const [upgradeLookingFor, setUpgradeLookingFor] = useState(false);

  const [jsonModalOpen, setJsonModalOpen] = useState(false);

  const [autoFilling, setAutoFilling] = useState(false);

  const autoFillRanRef = useRef(false);

  const [focusQuestionId, setFocusQuestionId] = useState<string | null>(null);

  const questionNodeRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const onCloseRef = useRef(onClose);

  const onSubmittedRef = useRef(onSubmitted);

  const onAlreadyCompletedRef = useRef(onAlreadyCompleted);



  onCloseRef.current = onClose;

  onSubmittedRef.current = onSubmitted;

  onAlreadyCompletedRef.current = onAlreadyCompleted;



  const generating = loadPhase === "generating";

  const loading = loadPhase === "loading";

  const loadFailed = loadPhase === "error";

  const theme = useMantineTheme();

  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`, false, {

    getInitialValueInEffect: true,

  });

  const isTablet = useMediaQuery(

    `(min-width: calc(${theme.breakpoints.sm} + 1px)) and (max-width: ${theme.breakpoints.lg})`,

    false,

    { getInitialValueInEffect: true },

  );

  const useFullScreen = isMobile;

  const isDesktop = !isMobile && !isTablet;

  const questionnaireReady = Boolean(questions);



  const runLoadQuestionnaire = useCallback(async (cancelled: () => boolean) => {

    try {

      const status = adminCompanyId

        ? await trpc.admin.getCompanyIntentStatus.query({ companyId: adminCompanyId })

        : managedCompanyId

          ? await trpc.facilitator.getManagedCompanyIntentStatus.query({ companyId: managedCompanyId })

          : await trpc.companyIntent.status.query();

      if (cancelled()) return;



      if (status.completed) {

        if (adminCompanyId && onAlreadyCompletedRef.current) {

          onAlreadyCompletedRef.current();

        } else if (onSubmittedRef.current) {

          onSubmittedRef.current();

        } else {

          onCloseRef.current();

        }

        return;

      }



      const isUpgrade = Boolean((status as { upgradeLookingFor?: boolean }).upgradeLookingFor);

      setUpgradeLookingFor(isUpgrade);



      const draftQuestions = status.draftQuestionsSnapshot?.questions;

      if (Array.isArray(draftQuestions) && draftQuestions.length > 0) {

        setQuestions(draftQuestions);

        setLoadPhase("ready");

        return;

      }



      setLoadPhase("generating");

      const res = await withTimeout(

        adminCompanyId

          ? trpc.admin.generateCompanyIntentQuestionnaire.mutate({ companyId: adminCompanyId })

          : managedCompanyId

            ? trpc.facilitator.generateManagedCompanyIntent.mutate({ companyId: managedCompanyId })

            : trpc.companyIntent.generate.mutate(),

        GENERATE_TIMEOUT_MS,

        "Questionnaire generation",

      );

      if (cancelled()) return;

      const snapshot = res.questionsSnapshot as { questions?: IntentQuestion[] } | null | undefined;
      setQuestions(Array.isArray(snapshot?.questions) ? snapshot.questions : []);

      setUpgradeLookingFor(Boolean((res as { upgradeLookingFor?: boolean }).upgradeLookingFor));

      setLoadPhase("ready");

    } catch {

      if (cancelled()) return;

      setQuestions(null);

      setAnswers({});

      setQuestionnaireFollowUpText("");

      setLoadPhase("error");

    }

  }, [adminCompanyId, managedCompanyId]);



  useEffect(() => {

    if (!opened) {

      setQuestions(null);

      setAnswers({});

      setQuestionnaireFollowUpText("");

      setLoadPhase("idle");

      setSubmitting(false);

      setUpgradeLookingFor(false);

      setJsonModalOpen(false);

      setAutoFilling(false);

      autoFillRanRef.current = false;

      setFocusQuestionId(null);

      questionNodeRefs.current = {};

      return;

    }



    let cancelled = false;

    setLoadPhase("loading");

    setQuestions(null);

    setAnswers({});

    setQuestionnaireFollowUpText("");

    void runLoadQuestionnaire(() => cancelled);

    return () => {

      cancelled = true;

    };

  }, [opened, retryNonce, runLoadQuestionnaire]);



  useEffect(() => {

    if (!questions) return;

    const q15 = questions.find((q) => q.id === Q15_PRIORITY_MARKET_ID);

    const catalogGroup = q15?.answerGroups?.find((g) => g.inputType === "catalog_geography");

    const suggestedRegions = resolveCollaborationRegionIds(catalogGroup?.suggestedRegionIds ?? []);

    if (!suggestedRegions.length) return;

    setAnswers((prev) => {

      const current = prev[Q15_PRIORITY_MARKET_ID];

      if (current?.groupRegionIds && Object.values(current.groupRegionIds).some((ids) => ids.length > 0)) {

        return prev;

      }

      const groupId = catalogGroup!.id;

      return {

        ...prev,

        [Q15_PRIORITY_MARKET_ID]: {

          selectedIds: current?.selectedIds ?? [],

          groupSelections: {

            ...(current?.groupSelections ?? {}),

          },

          groupRegionIds: {

            ...(current?.groupRegionIds ?? {}),

            [groupId]: suggestedRegions,

          },

        },

      };

    });

  }, [questions]);



  const progress = useMemo(() => {

    if (!questions) return { answered: 0, total: 0 };

    const answered = questions.reduce(

      (acc, q) =>

        isQuestionAnswerStateComplete(q, answers[q.id] ?? EMPTY_ANSWER) ? acc + 1 : acc,

      0,

    );

    return { answered, total: questions.length };

  }, [questions, answers]);



  const progressPct = progress.total ? Math.round((progress.answered / progress.total) * 100) : 0;

  const remaining = progress.total - progress.answered;

  const jsonItems = useMemo(

    () => (questions ? intentQuestionsToJsonSource(questions, answers) : []),

    [questions, answers],

  );



  const canSubmit = useMemo(() => {

    if (!questions || questions.length === 0) return false;

    return questions.every((q) => isQuestionAnswerStateComplete(q, answers[q.id] ?? EMPTY_ANSWER));

  }, [questions, answers]);



  const handleAutoFill = useCallback(async () => {

    if (!adminCompanyId || autoFilling) return;

    setAutoFilling(true);

    try {

      const res = await withTimeout(

        trpc.admin.autoFillCompanyIntentQuestionnaire.mutate({ companyId: adminCompanyId }),

        GENERATE_TIMEOUT_MS,

        "Questionnaire auto-fill",

      );

      const filled = (res as { answers?: IntentAnswers }).answers ?? {};

      const nextAnswers = { ...intentAnswersToUiState(filled) };

      setAnswers((prev) => ({ ...prev, ...nextAnswers }));

      setQuestionnaireFollowUpText(collectIntentFollowUpTexts(nextAnswers));

      successNotification({

        title: "Questionnaire auto-filled",

        message: "Review the selected options, change any that need it, then submit.",

      });

    } catch (err) {

      handleTrpcError(err);

    } finally {

      setAutoFilling(false);

    }

  }, [adminCompanyId, autoFilling]);



  useEffect(() => {

    if (!autoFillOnReady || !adminCompanyId) return;

    if (loadPhase !== "ready" || !questions?.length) return;

    if (autoFillRanRef.current || autoFilling) return;

    autoFillRanRef.current = true;

    void handleAutoFill();

  }, [autoFillOnReady, adminCompanyId, loadPhase, questions, autoFilling, handleAutoFill]);



  const handleSubmit = async () => {

    if (!questions || questions.length === 0) return;

    const firstIncomplete = questions.find(
      (q) => !isQuestionAnswerStateComplete(q, answers[q.id] ?? EMPTY_ANSWER),
    );

    if (firstIncomplete) {
      setFocusQuestionId(firstIncomplete.id);
      const node = questionNodeRefs.current[firstIncomplete.id];
      node?.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => {
        setFocusQuestionId((current) => (current === firstIncomplete.id ? null : current));
      }, 2400);
      return;
    }

    setSubmitting(true);

    try {

      const payload: IntentAnswers = {};

      for (const q of questions) {

        const state = answers[q.id] ?? EMPTY_ANSWER;

        const usesAnswerGroups = hasAnswerGroups(q.id) && Boolean(q.answerGroups?.length);

        payload[q.id] = toIntentAnswerPayload({

          selectedIds: state.selectedIds,

          selectionMode: resolveIntentSelectionMode(q.id, q.selectionMode),

          maxSelections: getMaxSelections(q.id),

          followUpText:
            q.id === Q15_PRIORITY_MARKET_ID ||
            (!questions.some((item) => item.id === Q15_PRIORITY_MARKET_ID) &&
              q.id === questions[questions.length - 1]?.id)
              ? questionnaireFollowUpText
              : undefined,

          groupSelections: state.groupSelections,

          groupRegionIds: state.groupRegionIds,

          conditionalAnswers: state.conditionalAnswers,

          usesAnswerGroups,

        });

      }



      if (adminCompanyId) {

        await trpc.admin.submitCompanyIntentQuestionnaire.mutate({

          companyId: adminCompanyId,

          answers: payload,

        });

      } else if (managedCompanyId) {

        await trpc.facilitator.submitManagedCompanyIntent.mutate({

          companyId: managedCompanyId,

          answers: payload,

        });

      } else {

        await trpc.companyIntent.submit.mutate({ answers: payload });

      }

      if (onSubmittedRef.current) {

        onSubmittedRef.current();

      } else {

        onCloseRef.current();

      }

    } catch {

      // submit errors still use default trpc handling elsewhere if needed

    } finally {

      setSubmitting(false);

    }

  };



  const handleRequestClose = () => {

    if (generating || submitting || autoFilling) return;

    onCloseRef.current();

  };



  const handleRetryGenerate = () => {

    setRetryNonce((n) => n + 1);

  };



  const questionnairePanelHeight = useFullScreen

    ? "calc(100dvh - 56px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))"

    : undefined;



  return (

    <>

    <Modal

      opened={opened}

      onClose={handleRequestClose}

      title={
        upgradeLookingFor
          ? "What are you looking for?"
          : adminCompanyId
            ? "Onboarding questionnaire"
            : "Build your collaboration profile"
      }

      centered

      fullScreen={useFullScreen}

      size={isDesktop ? "min(96vw, 1300px)" : isTablet ? "min(92vw, 900px)" : "lg"}

      zIndex={1200}

      withCloseButton={!generating && !autoFilling}

      closeOnClickOutside={!generating && !submitting && !autoFilling}

      closeOnEscape={!generating && !submitting && !autoFilling}

      styles={{

        inner: questionnaireReady ? { overflow: "hidden" } : undefined,

        content: questionnaireReady

          ? {

              display: "flex",

              flexDirection: "column",

              overflow: "hidden",

              ...(useFullScreen

                ? { height: "100dvh", maxHeight: "100dvh" }

                : {

                    height: "min(94dvh, 900px)",

                    maxHeight: "min(94dvh, 900px)",

                  }),

            }

          : undefined,

        header: {

          flexShrink: 0,

          ...(useFullScreen ? { paddingTop: "max(12px, env(safe-area-inset-top))" } : {}),

        },

        body: questionnaireReady

          ? {

              display: "flex",

              flexDirection: "column",

              flex: 1,

              minHeight: 0,

              overflow: "hidden",

              paddingBottom: useFullScreen ? "max(8px, env(safe-area-inset-bottom))" : 12,

            }

          : undefined,

      }}

    >

      {generating ? (

        <Stack align="center" py="xl" gap="sm">

          <Loader size="md" color="primary" type="dots" />

          <Text size="sm" c="dimmed" ta="center">

            {upgradeLookingFor
              ? "Generating what you are looking for..."
              : "Generating your questionnaire..."}

          </Text>

          <Text size="xs" c="dimmed" ta="center">

            This usually takes under a minute. Please keep this window open.

          </Text>

        </Stack>

      ) : loading ? (

        <Stack align="center" py="xl" gap="sm">

          <Loader size="sm" color="primary" type="dots" />

          <Text size="sm" c="dimmed">

            Loading your questionnaire...

          </Text>

        </Stack>

      ) : loadFailed ? (

        <AiGenerationErrorState

          message="Something went wrong while generating your questionnaire. Please generate again."

          onRetry={handleRetryGenerate}

          retrying={loading || generating}

          minHeight={240}

        />

      ) : questions ? (

        <Box

          style={{

            display: "flex",

            flexDirection: "column",

            height: questionnairePanelHeight ?? "100%",

            minHeight: 0,

            overflow: "hidden",

          }}

        >

          <Box

            style={{

              flexShrink: 0,

              borderRadius: "var(--mantine-radius-lg)",

              padding: "var(--mantine-spacing-md)",

              background: "var(--mantine-color-primary-7)",

            }}

          >

            <Group align="flex-start" gap="sm" wrap="nowrap">

              <ThemeIcon

                size={38}

                radius="md"

                variant="white"

                style={{ flexShrink: 0, color: "var(--mantine-color-primary-6)" }}

              >

                <IconSparkles size={22} />

              </ThemeIcon>

              <Text size="sm" c="white" style={{ opacity: 0.95, lineHeight: 1.45 }}>

                Tell us about your company so verified partners can assess fit. Your answers appear on

                your profile.

              </Text>

            </Group>



            {adminCompanyId ? (

              <Group justify="flex-end" mt="md">

                <Button

                  size="sm"

                  variant="white"

                  color="primary"

                  leftSection={<IconSparkles size={16} />}

                  onClick={() => void handleAutoFill()}

                  loading={autoFilling}

                  disabled={autoFilling || submitting}

                >

                  Fill questionnaire auto

                </Button>

              </Group>

            ) : null}



            <Group justify="space-between" mt="md" mb={6}>

              <Text size="xs" fw={600} c="white" style={{ letterSpacing: 0.3 }}>

                {progress.answered} of {progress.total} answered

              </Text>

              <Text size="xs" fw={700} c="white">

                {progressPct}%

              </Text>

            </Group>

            <Progress

              value={progressPct}

              size="md"

              radius="xl"

              transitionDuration={350}

              styles={{

                root: { backgroundColor: "rgba(255, 255, 255, 0.28)" },

                section: { background: "linear-gradient(90deg, #a5f3fc 0%, #ffffff 100%)" },

              }}

            />

          </Box>



          <ScrollArea

            type="auto"

            offsetScrollbars

            scrollbarSize={8}

            style={{ flex: 1, minHeight: 0 }}

            styles={{

              root: { flex: 1, minHeight: 0 },

              viewport: { paddingTop: "var(--mantine-spacing-md)", paddingBottom: 8 },

            }}

          >

            <Stack gap="md" pr="xs" pb="sm">

              {questions.map((q, index) => {

                const sectionHeader = getSectionHeader(q, questions[index - 1]);

                return (

                <Stack
                  key={q.id}
                  gap="xs"
                  ref={(node) => {
                    questionNodeRefs.current[q.id] = node;
                  }}
                  style={
                    focusQuestionId === q.id
                      ? {
                          outline: "2px solid var(--mantine-color-orange-5)",
                          outlineOffset: 4,
                          borderRadius: 16,
                        }
                      : undefined
                  }
                >

                  {sectionHeader ? (

                    <Text

                      size="xs"

                      fw={700}

                      c="primary.7"

                      tt="uppercase"

                      mt={index === 0 ? 0 : "sm"}

                      style={{ letterSpacing: 0.6 }}

                    >

                      {sectionHeader}

                    </Text>

                  ) : null}

                  <IntentQuestionCard

                    question={q}

                    index={index}

                    isDesktop={isDesktop}

                    answer={answers[q.id] ?? EMPTY_ANSWER}

                    onChange={(next) => {
                      setAnswers((prev) => ({ ...prev, [q.id]: next }));
                      if (focusQuestionId === q.id) setFocusQuestionId(null);
                    }}

                  />

                </Stack>

              );

              })}

              <Stack gap={6} mt="xs">
                <Text size="sm" fw={500} c="slate.7">
                  {QUESTIONNAIRE_FOLLOW_UP.prompt}
                </Text>
                <Textarea
                  value={questionnaireFollowUpText}
                  onChange={(event) =>
                    setQuestionnaireFollowUpText(event.currentTarget.value)
                  }
                  maxLength={QUESTIONNAIRE_FOLLOW_UP.maxLength ?? INTENT_FOLLOW_UP_MAX_CHARS}
                  minRows={3}
                  autosize
                  placeholder="Add context that helps a partner understand your answers"
                />
                <Text size="xs" c="dimmed" ta="right">
                  {questionnaireFollowUpText.length} /{" "}
                  {QUESTIONNAIRE_FOLLOW_UP.maxLength ?? INTENT_FOLLOW_UP_MAX_CHARS}
                </Text>
              </Stack>

            </Stack>

          </ScrollArea>



          <Group

            justify="space-between"

            wrap="wrap"

            gap="sm"

            style={{

              flexShrink: 0,

              borderTop: "1px solid var(--mantine-color-slate-2)",

              paddingTop: 12,

              marginTop: "var(--mantine-spacing-sm)",

              backgroundColor: "var(--mantine-color-body)",

            }}

          >

            <Text size="sm" c="slate.5">

              {canSubmit

                ? "All set! You can submit now."

                : `${remaining} question${remaining === 1 ? "" : "s"} left`}

            </Text>

            <Group gap="sm">

              {adminCompanyId ? (

                <Button

                  variant="default"

                  leftSection={<IconCode size={16} />}

                  onClick={() => setJsonModalOpen(true)}

                  disabled={submitting}

                >

                  JSON

                </Button>

              ) : null}

            <Button

              variant="filled"

              color="primary"

              onClick={handleSubmit}

              loading={submitting}

              disabled={submitting}

            >

              Submit

            </Button>

            </Group>

          </Group>

        </Box>

      ) : null}

    </Modal>

    {adminCompanyId ? (

      <AdminIntentQuestionnaireJsonModal

        opened={jsonModalOpen}

        onClose={() => setJsonModalOpen(false)}

        companyId={adminCompanyId}

        items={jsonItems}

        additionalInfo=""

        submitMode="submit"

        canImportQuestions={!upgradeLookingFor}

        onApplied={() => {

          setJsonModalOpen(false);

          if (onAlreadyCompletedRef.current) {

            onAlreadyCompletedRef.current();

          } else if (onSubmittedRef.current) {

            onSubmittedRef.current();

          } else {

            onCloseRef.current();

          }

        }}

      />

    ) : null}

    </>

  );

}


